import { useEffect, useState } from "react";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { ClearableInput } from "../common/ClearableInput";

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const today = () => ymd(new Date());
const monthStart = () => { const d = new Date(); return ymd(new Date(d.getFullYear(), d.getMonth(), 1)); };
const num = (v) => Number(v || 0).toLocaleString("en-IN");

export default function DprMemberProgress({ pid }) {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => {
    if (!pid || !from || !to) { setData(null); return; }
    setData(null);
    api.get("/dpr/member-progress", { params: { project_id: pid, date_from: from, date_to: to } })
      .then((r) => setData(r.data)).catch(() => setData({ activities: [], rows: [] }));
  }, [pid, from, to]);
  const exportXlsx = async () => {
    setBusy(true);
    try {
      await api.post("/reports/exports", { report_type: "DPR_MEMBER_PROGRESS", filters: { project_id: pid, date_from: from, date_to: to }, idempotency_key: crypto.randomUUID() });
      toast.success("Export requested — download it from the Download Center when ready.");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not request the export"); }
    setBusy(false);
  };
  const acts = data?.activities || [];
  const sq = search.trim().toLowerCase();
  const rows = (data?.rows || []).filter((r) => !sq || [r.member_code, r.member_name, r.teams].some((v) => (v || "").toLowerCase().includes(sq)));
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3 rounded-lg border border-slate-200 bg-white p-3">
        <div><Label className="text-xs text-slate-500">From</Label><Input type="date" data-testid="dpr-mp-from" value={from} onChange={(e) => setFrom(e.target.value)} className="mt-1 h-9" /></div>
        <div><Label className="text-xs text-slate-500">To</Label><Input type="date" data-testid="dpr-mp-to" value={to} onChange={(e) => setTo(e.target.value)} className="mt-1 h-9" /></div>
        <Button size="sm" variant="outline" className="h-9" data-testid="dpr-mp-today" onClick={() => { setFrom(today()); setTo(today()); }}>Today</Button>
        <Button size="sm" variant="outline" className="h-9" data-testid="dpr-mp-month" onClick={() => { setFrom(monthStart()); setTo(today()); }}>This Month</Button>
        <div><Label className="text-xs text-slate-500">Search</Label><ClearableInput testid="dpr-mp-search" value={search} onChange={setSearch} placeholder="Member code / name / team" className="mt-1 w-56" /></div>
        <Button size="sm" className="ml-auto h-9 bg-teal-700 hover:bg-teal-800" data-testid="dpr-mp-export" disabled={!pid || busy || !data?.rows?.length} onClick={exportXlsx}>
          {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-4 w-4" />}Export Excel
        </Button>
      </div>
      <p className="text-xs text-slate-500">Each present member gets the full activity count of the team they worked in that day (present teams only).</p>
      {!pid ? <p className="text-sm text-slate-400">Select a project.</p> : !data ? <p className="text-sm text-slate-400">Loading…</p> : !data.rows.length ? (
        <p className="rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-400" data-testid="dpr-mp-empty">No member activity in this period.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="dpr-mp-table">
            <thead>
              <tr className="border-b bg-slate-50 text-xs uppercase text-slate-500">
                <th className="px-3 py-2 text-left">Member Code</th><th className="px-3 py-2 text-left">Member Name</th><th className="px-3 py-2 text-left">Team(s)</th><th className="px-3 py-2 text-right">Days</th>
                {acts.map((a) => <th key={a.id} className="whitespace-nowrap px-3 py-2 text-right">{a.name}</th>)}
                <th className="px-3 py-2 text-right text-teal-700">Total</th>
              </tr>
            </thead>
            <tbody>
              {!rows.length && <tr><td colSpan={acts.length + 5} className="px-3 py-4 text-center text-sm text-slate-400" data-testid="dpr-mp-nomatch">No member matches “{search}”.</td></tr>}
              {rows.map((r) => (
                <tr key={r.member_id} className="border-b last:border-0" data-testid={`dpr-mp-row-${r.member_id}`}>
                  <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-slate-600" data-testid={`dpr-mp-code-${r.member_id}`}>{r.member_code || "–"}</td>
                  <td className="whitespace-nowrap px-3 py-2 font-medium text-slate-800">{r.member_name}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.teams}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.days_present}</td>
                  {acts.map((a) => <td key={a.id} className="px-3 py-2 text-right tabular-nums text-slate-600">{num(r.activities[a.id]) === "0" ? "–" : num(r.activities[a.id])}</td>)}
                  <td className="px-3 py-2 text-right font-bold tabular-nums text-teal-800">{num(r.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
